import { useCallback, type JSX } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { EmptyState } from "@mailflare/ui";
import {
  SYSTEM_FOLDERS,
  type LabelId,
  type MailboxWithCounts,
  type MessageId,
  type SystemFolder,
} from "@mailflare/shared";

import { useAppShell } from "@/app/layout/app-shell";
import { folderLabel } from "@/lib/utils";

import { useMessages } from "./hooks";
import { MailWorkspace } from "./mail-workspace";

function isSystemFolder(value: string | undefined): value is SystemFolder {
  return value !== undefined && (SYSTEM_FOLDERS as readonly string[]).includes(value);
}

interface ViewConfig {
  readonly args: {
    readonly folder?: SystemFolder;
    readonly labelId?: LabelId;
    readonly isFlagged?: boolean;
  };
  readonly label: string;
  readonly emptyTitle: string;
  readonly emptyDescription: string;
}

function resolveView(
  pathname: string,
  folderParam: string | undefined,
  labelId: LabelId | undefined,
): ViewConfig {
  if (pathname.endsWith("/starred")) {
    return {
      args: { isFlagged: true },
      label: "Starred",
      emptyTitle: "No starred messages",
      emptyDescription: "Star messages to find them here.",
    };
  }
  if (labelId !== undefined) {
    return {
      args: { labelId },
      label: "Label",
      emptyTitle: "No messages with this label",
      emptyDescription: "Apply this label from the reading pane to organise mail.",
    };
  }
  const folder: SystemFolder = isSystemFolder(folderParam) ? folderParam : "inbox";
  return {
    args: { folder },
    label: folderLabel(folder),
    emptyTitle: `No messages in ${folderLabel(folder)}`,
    emptyDescription: "When mail arrives it will appear here.",
  };
}

function MailboxWorkspace({
  mailbox,
  view,
  selectedId,
  onSelect,
}: {
  readonly mailbox: MailboxWithCounts;
  readonly view: ViewConfig;
  readonly selectedId: MessageId | null;
  readonly onSelect: (id: MessageId | null) => void;
}): JSX.Element {
  const query = useMessages({ mailboxId: mailbox.id, ...view.args });
  const messages = query.data?.pages.flatMap((page) => page.messages) ?? [];

  return (
    <MailWorkspace
      mailbox={mailbox}
      messages={messages}
      listLabel={view.label}
      emptyTitle={view.emptyTitle}
      emptyDescription={view.emptyDescription}
      isLoading={query.isLoading}
      isError={query.isError}
      onRetry={() => void query.refetch()}
      hasNextPage={query.hasNextPage}
      isFetchingNextPage={query.isFetchingNextPage}
      onLoadMore={() => void query.fetchNextPage()}
      selectedId={selectedId}
      onSelect={onSelect}
    />
  );
}

export function MailboxPage(): JSX.Element {
  const { mailbox } = useAppShell();
  const params = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const folderParam = params["folder"];
  const labelId = params["labelId"] as LabelId | undefined;
  const selectedId = (searchParams.get("message") as MessageId | null) ?? null;

  const setSelected = useCallback(
    (id: MessageId | null): void => {
      const next = new URLSearchParams(searchParams);
      if (id === null) next.delete("message");
      else next.set("message", id);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  if (mailbox === null) {
    return (
      <EmptyState
        title="Mailbox not found"
        description="This mailbox is unavailable."
        className="m-auto"
      />
    );
  }

  return (
    <MailboxWorkspace
      mailbox={mailbox}
      view={resolveView(location.pathname, folderParam, labelId)}
      selectedId={selectedId}
      onSelect={setSelected}
    />
  );
}
